import _, { List } from 'lodash';
import { PromiseState } from 'react-refetch';

import { MaybeUndef, StringMap } from '../../types/system';
import { Action, Reducer } from '../redux.types';

import {
  createPayloadExtractor,
  PayloadExtractorOptions,
} from './list-payload-extractor';
import {
  GetListErrorPayload,
  GetListSuccessPayload,
  PossibleIdentityKeysOf,
} from './list.types';

export interface ListState<T> {
  itemsMap: StringMap<T>;
  idsOrdered: string[];
  loadState: PromiseState;
}

export const createDefaultListState = <T extends object>(): ListState<T> => ({
  itemsMap: {},
  idsOrdered: [],
  loadState: PromiseState.create(),
});

export interface ListReducerOptions<T extends object> {
  actionNames?: [string, string, string];
  payloadExtractorOptions?: PayloadExtractorOptions;
  key: PossibleIdentityKeysOf<T>;
}

export const createListReducer = <T extends object>(
  options: ListReducerOptions<T>,
): Reducer<ListState<T>> => {
  const { actionNames, payloadExtractorOptions, key } = options;
  const payloadExtractor = createPayloadExtractor<
    GetListSuccessPayload<T>,
    GetListErrorPayload
  >(payloadExtractorOptions);

  return (
    rawState: MaybeUndef<ListState<T>>,
    { type, payload }: Action,
  ): ListState<T> => {
    const state =
      rawState === undefined ? createDefaultListState<T>() : rawState;

    if (!actionNames) {
      return state;
    }

    const [getStartActionName, getSuccessActionName, getErrorActionName] =
      actionNames;

    switch (type) {
      case getStartActionName: {
        return {
          idsOrdered: [],
          itemsMap: {},
          loadState: PromiseState.create(),
        };
      }

      case getSuccessActionName: {
        const list: List<T> =
          payloadExtractor.extractListSuccessPayload(payload);
        const itemsMap: StringMap<T> = _.keyBy<T>(list, key);

        return {
          idsOrdered: _.map<T>(list, key),
          itemsMap,
          loadState: PromiseState.resolve(),
        };
      }

      case getErrorActionName: {
        const { error }: GetListErrorPayload =
          payloadExtractor.extractListErrorPayload(payload);

        return {
          idsOrdered: [],
          itemsMap: {},
          loadState: PromiseState.reject(error),
        };
      }

      default:
        return state;
    }
  };
};
